/**
 * Project ICE Facade (100% Shared)
 *
 * One facade for a whole project's scopes: the score each scope carries, the
 * reasoning card behind it, and the queue-then-poll protocol for evaluating
 * any of them. Scoped to the project rather than a single scope because the
 * work-scopes list shows every scope at once and can have several evaluations
 * running side by side.
 *
 * Source: docs/Integration/ICE-Scores-Frontend-Guide.md §3–§4, §8.
 *
 * @module useProjectIceFacade
 * @category Facades/Shared
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { iceService } from '../../../api/services/ice.service';
import type { IceRankedScope, IceScopeResponse } from '../../../types/ice.types';

// ==================== Poll cadence ====================

/** Poll every 3s for the first minute… */
const POLL_INTERVAL_MS = 3_000;
/** …then back off, because three agents run sequentially — minutes, not seconds. */
const POLL_INTERVAL_SLOW_MS = 10_000;
const BACKOFF_AFTER_MS = 60_000;
/** The backend's in-flight record expires at 15 minutes; stop with it. */
const POLL_TIMEOUT_MS = 15 * 60_000;
/**
 * Right after the POST the `GET` can answer before the in-flight record is
 * visible. Inside this window "no in_flight yet" means starting, not gone.
 */
const MISSING_IN_FLIGHT_GRACE_MS = 20_000;

const FAILURE_MESSAGE = 'The evaluation did not finish. Try again.';

// ==================== Types ====================

export interface ProjectIceFacade {
  /** Ranked rows keyed by scope — the badge score for each list row. */
  scoresByScopeId: Record<string, IceRankedScope>;
  /** Scopes with no evaluation yet. Never render these as rank-last / score-zero. */
  unrankedScopeIds: string[];
  /** Cached `GET /ice` responses, filled when a card is opened or a run lands. */
  cardsByScopeId: Record<string, IceScopeResponse>;
  /** Scopes with an evaluation queued or running right now. */
  scoringScopeIds: string[];
  /** Founder-safe per-scope copy (queue failures, run failures). */
  errorsByScopeId: Record<string, string>;
  isLoading: boolean;
  /** Scopes whose card is being fetched for display. */
  loadingCardScopeIds: string[];
  error: string | null;
  /** Reload the ranking. Reading never computes. */
  refresh: () => Promise<void>;
  /** Fetch one scope's card. Read-only — this never queues an evaluation. */
  loadCard: (scopeId: string) => Promise<void>;
  /** Queue an evaluation and poll it to completion. The only thing that scores. */
  evaluate: (scopeId: string) => Promise<void>;
  clearScopeError: (scopeId: string) => void;
}

export interface UseProjectIceOptions {
  projectId: string | null | undefined;
  /** Load the ranking on mount and whenever the project changes. Defaults to true. */
  autoFetch?: boolean;
}

interface PollState {
  evaluationId: string;
  startedAt: number;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * Provides every scope's ICE state for one project.
 *
 * Nothing here scores implicitly: `refresh` and `loadCard` only read, and only
 * `evaluate` queues a run.
 */
export function useProjectIceFacade({
  projectId,
  autoFetch = true,
}: UseProjectIceOptions): ProjectIceFacade {
  const [ranked, setRanked] = useState<IceRankedScope[]>([]);
  const [unrankedScopeIds, setUnrankedScopeIds] = useState<string[]>([]);
  const [cardsByScopeId, setCardsByScopeId] = useState<Record<string, IceScopeResponse>>({});
  const [scoringScopeIds, setScoringScopeIds] = useState<string[]>([]);
  const [loadingCardScopeIds, setLoadingCardScopeIds] = useState<string[]>([]);
  const [errorsByScopeId, setErrorsByScopeId] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const projectIdRef = useRef<string | null>(projectId ?? null);
  projectIdRef.current = projectId ?? null;

  /** One entry per scope with a run in flight. */
  const pollsRef = useRef<Map<string, PollState>>(new Map());
  const mountedRef = useRef(true);

  const stopPolling = useCallback((scopeId: string) => {
    const poll = pollsRef.current.get(scopeId);
    if (poll?.timer) clearTimeout(poll.timer);
    pollsRef.current.delete(scopeId);
    setScoringScopeIds((prev) => prev.filter((id) => id !== scopeId));
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const polls = pollsRef.current;
    return () => {
      mountedRef.current = false;
      polls.forEach((poll) => poll.timer && clearTimeout(poll.timer));
      polls.clear();
    };
  }, []);

  const setScopeError = useCallback((scopeId: string, message: string) => {
    setErrorsByScopeId((prev) => ({ ...prev, [scopeId]: message }));
  }, []);

  const clearScopeError = useCallback((scopeId: string) => {
    setErrorsByScopeId((prev) => {
      if (!(scopeId in prev)) return prev;
      const next = { ...prev };
      delete next[scopeId];
      return next;
    });
  }, []);

  // ==================== Ranking ====================

  const refresh = useCallback(async () => {
    const id = projectIdRef.current;
    if (!id) return;

    setIsLoading(true);
    setError(null);
    try {
      const data = await iceService.getProjectRanking(id);
      if (!mountedRef.current || projectIdRef.current !== id) return;
      setRanked(data.ranked ?? []);
      setUnrankedScopeIds(data.unranked_scope_ids ?? []);
    } catch (err) {
      console.error('Failed to load ICE ranking:', err);
      if (!mountedRef.current || projectIdRef.current !== id) return;
      setError('Could not load ICE scores.');
      setRanked([]);
      setUnrankedScopeIds([]);
    } finally {
      if (mountedRef.current && projectIdRef.current === id) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!autoFetch || !projectId) return;
    void refresh();
  }, [autoFetch, projectId, refresh]);

  // ==================== Polling ====================

  // `poll` and `schedulePoll` call each other; the ref keeps `schedulePoll`
  // stable so a running timer chain survives re-renders.
  const pollRef = useRef<(scopeId: string) => Promise<void>>(async () => {});

  const schedulePoll = useCallback((scopeId: string, delay: number) => {
    const poll = pollsRef.current.get(scopeId);
    if (!poll) return;
    if (poll.timer) clearTimeout(poll.timer);
    poll.timer = setTimeout(() => {
      const live = pollsRef.current.get(scopeId);
      if (live) live.timer = null;
      void pollRef.current(scopeId);
    }, delay);
  }, []);

  /**
   * One poll tick for one scope.
   *
   * Completion is an exact `current.id === evaluation_id` match — on a
   * re-evaluation the GET keeps serving the previous score until the new row
   * lands, so nothing else distinguishes the two responses.
   */
  const poll = useCallback(
    async (scopeId: string) => {
      const state = pollsRef.current.get(scopeId);
      const project = projectIdRef.current;
      if (!state || !project || !mountedRef.current) return;

      const elapsed = Date.now() - state.startedAt;
      if (elapsed > POLL_TIMEOUT_MS) {
        stopPolling(scopeId);
        setScopeError(scopeId, FAILURE_MESSAGE);
        return;
      }

      let data: IceScopeResponse | null;
      try {
        data = await iceService.getScopeIce(project, scopeId);
      } catch (err) {
        // A transient read failure is not a failed evaluation — keep polling.
        console.error('ICE poll failed:', err);
        schedulePoll(scopeId, elapsed > BACKOFF_AFTER_MS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_MS);
        return;
      }

      if (!mountedRef.current || pollsRef.current.get(scopeId) !== state) return;

      if (data) {
        setCardsByScopeId((prev) => ({ ...prev, [scopeId]: data as IceScopeResponse }));
      }

      if (data?.current?.id === state.evaluationId) {
        stopPolling(scopeId);
        clearScopeError(scopeId);
        // The badge score comes from the ranking, so pull the new one in.
        void refresh();
        return;
      }

      // The only way a failure becomes visible — a slow success and a dead run
      // look identical otherwise.
      if (data?.in_flight?.status === 'failed') {
        stopPolling(scopeId);
        setScopeError(scopeId, data.in_flight.error || FAILURE_MESSAGE);
        return;
      }

      // The record disappears ~5 minutes after it lands, so "no in_flight and
      // the current id still isn't mine" is a failure too — once past the
      // window where the record may simply not exist yet.
      if (!data?.in_flight && elapsed > MISSING_IN_FLIGHT_GRACE_MS) {
        stopPolling(scopeId);
        setScopeError(scopeId, FAILURE_MESSAGE);
        return;
      }

      schedulePoll(scopeId, elapsed > BACKOFF_AFTER_MS ? POLL_INTERVAL_SLOW_MS : POLL_INTERVAL_MS);
    },
    [clearScopeError, refresh, schedulePoll, setScopeError, stopPolling]
  );

  pollRef.current = poll;

  const startPolling = useCallback(
    (scopeId: string, evaluationId: string) => {
      const existing = pollsRef.current.get(scopeId);
      if (existing?.timer) clearTimeout(existing.timer);
      pollsRef.current.set(scopeId, { evaluationId, startedAt: Date.now(), timer: null });
      setScoringScopeIds((prev) => (prev.includes(scopeId) ? prev : [...prev, scopeId]));
      schedulePoll(scopeId, POLL_INTERVAL_MS);
    },
    [schedulePoll]
  );

  // ==================== Reads and the one write ====================

  const loadCard = useCallback(
    async (scopeId: string) => {
      const project = projectIdRef.current;
      if (!project || !scopeId) return;

      setLoadingCardScopeIds((prev) => (prev.includes(scopeId) ? prev : [...prev, scopeId]));
      try {
        const data = await iceService.getScopeIce(project, scopeId);
        if (!mountedRef.current || projectIdRef.current !== project) return;

        if (data) {
          setCardsByScopeId((prev) => ({ ...prev, [scopeId]: data }));
          // A run started elsewhere (another tab, or before a reload) is
          // adopted so the row shows progress instead of looking idle.
          if (data.in_flight?.status === 'running' && !pollsRef.current.has(scopeId)) {
            startPolling(scopeId, data.in_flight.evaluation_id);
          }
        } else {
          // 404 — never evaluated and nothing running. An ordinary empty state.
          setCardsByScopeId((prev) => {
            const next = { ...prev };
            delete next[scopeId];
            return next;
          });
        }
      } catch (err) {
        console.error('Failed to load ICE card:', err);
        if (mountedRef.current) setScopeError(scopeId, 'Could not load the ICE details.');
      } finally {
        if (mountedRef.current) {
          setLoadingCardScopeIds((prev) => prev.filter((id) => id !== scopeId));
        }
      }
    },
    [setScopeError, startPolling]
  );

  const evaluate = useCallback(
    async (scopeId: string) => {
      const project = projectIdRef.current;
      if (!project || !scopeId) return;
      // Already running here — a second press must not queue a second run.
      if (pollsRef.current.has(scopeId)) return;

      clearScopeError(scopeId);
      // Mark it scoring before the POST resolves so the row reacts immediately.
      setScoringScopeIds((prev) => (prev.includes(scopeId) ? prev : [...prev, scopeId]));

      try {
        const result = await iceService.evaluateScope(project, scopeId);
        if (!mountedRef.current) return;

        if (result.outcome === 'queue_unavailable') {
          // Definitive: nothing is running and nothing will arrive, so polling
          // anyway would poll forever.
          setScoringScopeIds((prev) => prev.filter((id) => id !== scopeId));
          setScopeError(scopeId, result.message);
          return;
        }

        // A 409 is indistinguishable from a normal press for the founder: the
        // response carries the running evaluation's id, so adopt and poll it.
        startPolling(scopeId, result.data.evaluation_id);
      } catch (err) {
        console.error('Failed to queue ICE evaluation:', err);
        if (!mountedRef.current) return;
        setScoringScopeIds((prev) => prev.filter((id) => id !== scopeId));
        setScopeError(scopeId, 'Could not start the evaluation. Try again.');
      }
    },
    [clearScopeError, setScopeError, startPolling]
  );

  const scoresByScopeId = useMemo(
    () => Object.fromEntries(ranked.map((row) => [row.scope_id, row])),
    [ranked]
  );

  return {
    scoresByScopeId,
    unrankedScopeIds,
    cardsByScopeId,
    scoringScopeIds,
    errorsByScopeId,
    isLoading,
    loadingCardScopeIds,
    error,
    refresh,
    loadCard,
    evaluate,
    clearScopeError,
  };
}
